import { Inject, Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { OEE } from "./models/OEE";
import { INFO } from "./models/INFO";
import { DB } from "./models/DB";
import * as pako from "pako";
import { PROD } from "./models/PROD";
import {
  ArrayQueue,
  ConstantBackoff,
  RetryEventDetail,
  Websocket,
  WebsocketBuilder,
  WebsocketEvent,
} from "websocket-ts";
import { NETSTAT } from "app/services/e-connect-web-socket/models/NETSTAT";

//Add models and

@Injectable({
  providedIn: "root",
})
export class WebSocketLocalService {
  public messageData: Subject<any> = new Subject();

  public retryConnection: boolean = true;
  public isRetryConnection: boolean = false;

  public recievedPong: Date;
  public recievedPongInfo: Date;

  /*****/
  readonly CLOSED: number = 0;
  readonly CONNECTING: number = 1;
  /*****/
  public connectionTimer: any;
  public pingConnectionTimer: any;

  public connectionTimerInfo: any;
  public pingConnectionTimerInfo: any;

  public webSocket: Websocket | null;
  public countSend: number = 0;
  public countSendInfo: number = 0;

  public accessToken: string = "";

  public onOpenConnection: Observable<boolean>;
  public onOpenConnectionSubject: BehaviorSubject<boolean> =
    new BehaviorSubject(false);

  constructor(@Inject("env") protected env: any) {
    this.onOpenConnection = this.onOpenConnectionSubject.asObservable();
  }

  public onConnect() {
    try {
      const self = this;
      self.retryConnection = true;
      self.webSocket = new WebsocketBuilder(self.env.webSocketLocalURL)
        .onOpen((i, ev) => {
          self.onOpen(i, ev);
        })
        .onClose((i, ev) => {
          self.onClose(i, ev);
        })
        .onError((i, ev) => {
          self.onError(i, ev);
        })
        .onMessage((i, ev) => {
          self.onMessage(i, ev);
        })
        .onRetry((i, ev) => {
          self.onRetry(i, ev);
        })
        .onReconnect((i, ev) => {
          self.onOpen(i, ev);
        })
        .withBuffer(new ArrayQueue())
        .withBackoff(new ConstantBackoff(1000))
        .build();
    } catch (e) {
      // console.log(e);
    }
  }

  private onOpen(socketInstance: Websocket, event: Event): void {
    const self = this;

    self.recievedPong = new Date();
    self.send("INFO", true);
    self.checkPing();
    this.onOpenConnectionSubject.next(true);
  }

  private onClose(socketInstance: Websocket, event: CloseEvent): void {
    const self = this;

    self.messageData.next("DISCONNECTED");
    clearInterval(self.pingConnectionTimer);
    self.pingConnectionTimer = null;
    self.isRetryConnection = false;
  }

  private onError(socketInstance: Websocket, event: Event): void {
    const self = this;
  }

  private onRetry(
    socketInstance: Websocket,
    event: CustomEvent<RetryEventDetail>
  ): void {
    const self = this;
  }

  public onMessage(socketInstance: Websocket, event: MessageEvent) {
    //this.webSocket = socketInstance;
    this.recievedPong = new Date();
    const message: string = event.data;
    let data;
    try {
      data = message.includes("END4STR_")
        ? JSON.parse(this.decode(message))
        : JSON.parse(message);
    } catch (_) {
      try {
        data = JSON.parse(message);
      } catch (_) {}
    }
    if (data?.cmd == "INFO") {
      let info = new INFO();
      info.centralConnected = data.centralConnected;
      info.ipaddress = data.ipaddress;
      info.mac = data.mac;
      info.processor = data.processor;
      info.sn = data.sn;
      info.type = data.type;
      info.version = data.version;
      info.availableCommands = data.availableCommands;
      info.currentMode = data.currentMode;
      this.messageData.next(info);
    } else if (data?.cmd == "OEE") {
      const oee = new OEE();
      oee.availability = data.Availability;
      oee.bestCadence = data.BestCadence;
      oee.cadence = data.Cadence;
      oee.count = data.Count;
      oee.counter1 = data.Counter1;
      oee.counter2 = data.Counter2;
      oee.cycleInfo = data.CycleInfo;
      oee.downtime = data.Downtime;
      oee.last24Hours = data.Last24Hours;
      oee.lastHour = data.LastHour;
      oee.lastMinute = data.LastMinute;
      oee.minutesElapsed = data.MinutesElapsed;
      oee.ngcount = data.NGCount;
      oee.OEE = data.OEE * 100;
      oee.OEELast7Days = data.OEELast7Days;
      oee.OEELast8Hours = data.OEELast8Hours;
      oee.operatingTime = data.OperatingTime;
      oee.performance = data.Performance;
      oee.quality = data.Quality;
      oee.reference = data.Reference;
      oee.cmd = data.cmd;
      oee.events = data.events;
      oee.pendingDowntimes = data.pendingDowntimes;
      oee.sn = data.sn;
      this.messageData.next(oee);
    } else if (data?.cmd == "PROD") {
      let prod = new PROD();

      prod.SerialNumber = data.sn;
      prod.ProdDataInici = data["1"];
      prod.ProdDataFi = data["2"];
      prod.TotalPiecesNG = Number(data["3"]);
      prod.Side = Number(data["4"]);
      prod.Quantity = Number(data["5"]);
      this.messageData.next(prod);
    } else if (data?.cmd == "IDLE") {
      let idle = { cmd: "IDLE", status: data.sn };
      this.messageData.next(idle);
    } else if (data?.cmd == "DB") {
      data = JSON.parse(this.decode(data[1]));
      let db = new DB();
      db.id = data.id;
      db.name = data.name;
      db.sn = data.sn;
      db.timestamp = data.timestamp;
      db.data = data.data;
      this.messageData.next(db);
    } else if (data?.cmd == "ERR") {
      this.messageData.next(data.msg);
    } else if (data?.cmd == "NETSTAT") {
      let netstat = new NETSTAT();
      netstat.SerialNumber = data.sn;
      netstat.Date = data["1"];
      netstat.networks = JSON.parse(
        this.decode(data["2"])
          .replace(/\n/g, "")
          .replace("eth0", '"eth0"')
          .replace("eth1", '"eth1"')
          .replace("wlan0", '"wlan0"')
          .replace("wwan0", '"wwan0"')
      );
      this.messageData.next(netstat);
    }
  }

  public send(message: string, isInfo = false, callback = undefined) {
    const self = this;
    if (this.webSocket) {
      self.webSocket?.send(message);
    }
  }

  public connect(isInfo = false) {
    // if(isInfo){
    //   this.onConnectInfo();
    // }else{
    //   this.onConnect();
    // }
    this.onConnect();
  }

  public parseDate(date: string) {
    if (date != null) {
      let splited = date.split("T");
      let year = splited[0].substring(0, 4);
      let month = splited[0].substring(4, 6);
      let day = splited[0].substring(6, 8);
      let hour = splited[1].substring(0, 2);
      let minute = splited[1].substring(2, 4);
      let seconds = splited[1].substring(4, 6);
      return new Date(
        year +
          "-" +
          month +
          "-" +
          day +
          "T" +
          hour +
          ":" +
          minute +
          ":" +
          seconds
      );
    } else {
      return null;
    }
  }

  public decode(encoded: string) {
    let code = encoded.split("END4STR_")[1];

    let strData = window.atob(code);

    let charData = strData.split("").map(function (x) {
      return x.charCodeAt(0);
    });
    let binData = new Uint8Array(charData);
    let data = pako.inflate(binData);
    return String.fromCharCode.apply(null, Array.from(new Uint16Array(data)));
  }

  public encode(decoded: string) {
    let binData = pako.deflate(decoded);
    let charData: Array<number> = Array.from(binData);
    let binaryString = String.fromCharCode(...charData);
    let encoded = window.btoa(binaryString);
    return encoded;
  }

  public Activ(deviceId: string, bActive: boolean) {
    let activeDevices = window.sessionStorage.getItem("ActiveDevices");
    if (bActive) {
      if (activeDevices == null) {
        let activeDevices2: string[] = [];
        activeDevices2.push(deviceId);
        let activeDevices3 = JSON.stringify(activeDevices2);
        window.sessionStorage.setItem("ActiveDevices", activeDevices3);
      } else {
        let activeDevices2: string[] = JSON.parse(activeDevices);
        let index = activeDevices2.findIndex((elem) => {
          return elem == deviceId;
        });

        if (index == -1) {
          activeDevices2.push(deviceId);
          let activeDevices3 = JSON.stringify(activeDevices2);
          window.sessionStorage.setItem("ActiveDevices", activeDevices3);
        }
      }
    } else {
      if (activeDevices != null) {
        let activeDevices2: string[] = JSON.parse(activeDevices);
        let index = activeDevices2.findIndex((elem) => {
          return elem == deviceId;
        });

        if (index != -1) {
          activeDevices2.splice(index, 1);
          let activeDevices3 = JSON.stringify(activeDevices2);
          window.sessionStorage.setItem("ActiveDevices", activeDevices3);
        }
      }
    }
    let sActiv = "ACTIV " + deviceId + ";" + (bActive ? "1" : "0");
    this.send(sActiv);
  }

  public checkPing() {
    let self = this;
    clearInterval(self.pingConnectionTimer);
    self.pingConnectionTimer = setInterval(() => {
      self.send("INFO", true);
      let now: Date = new Date();
      now.setSeconds(now.getSeconds() - 20);
      if (self.recievedPong < now && self.webSocket != undefined) {
        // check if the the last received message is greater than the current time by 40 seconds
        self.webSocket.close();
        //Quan tanquem la connexio per ping, la tornem a obrir
        self.onConnect();
      }
    }, 10000); //Check every 10 seconds
  }

  public closeConnection() {
    if (this.webSocket != undefined && this.webSocket != null) {
      this.retryConnection = false;
      this.webSocket.close();
      this.webSocket = null;
      clearInterval(this.pingConnectionTimer);
      this.pingConnectionTimer = null;
    }
  }
}
